/** How the buttons of what stands on a tile look: harvest, gather, load, info, the hub's card. */
import { KIND, type Cost } from '@bramvasterd/terrella-rules';

import { MACHINE_COLOR, RESOURCE_COLOR } from '@core/palette';

import type { TileOption } from '@game/options/options';
import type { MachineInfo } from '@game/planet/machines';
import { costEntries } from '@game/planet/pockets';
import type { TileFacts } from '@game/planet/tile';
import { machineName, resourceName, t } from '@i18n/index';
import type { ActionLook, Gauge } from '@ui/components/actionLook';

import { blueprintName } from '../build/blueprintText';

/** A CSS colour (#rrggbb) as a number for the hexagon art; none without one. */
const tintOf = (color: string | undefined): number | undefined =>
  color ? Number.parseInt(color.slice(1), 16) : undefined;

/** The tiles within an extractor's range: its own and the rings around it. */
const reachOf = (range: number): number => 1 + 3 * range * (range + 1);

const share = (a: number, b: number): number => (b > 0 ? Math.min(1, a / b) : 0);

/**
 * A machine's values at a glance: for an extractor the ring how full it is, the bars that, its
 * intake against its most and its ground against its reach; for a charge the ring and its bar how
 * far its fuse has burnt; for an infuser the ring how full its hopper is, the bars that and how
 * far the block under way has come.
 */
function gaugeOf(m: MachineInfo): Gauge {
  if (m.role === 'charge') {
    const burnt = 1 - share(m.left, m.fuseMs);
    return { ring: burnt, bars: [burnt] };
  }
  if (m.role === 'infuser') {
    const full = share(m.hopper.length, m.capacity);
    const laid = m.lay ? 1 - share(m.lay.left, m.lay.ms) : 0;
    return { ring: full, bars: [full, laid] };
  }
  const full = share(m.stored, m.storage);
  return { ring: full, bars: [full, share(m.rate, m.ingest), share(m.ground, reachOf(m.range))] };
}

/** What a machine holds, in words: "24 stone", "3 stone blocks", or for several kinds "5 blocks". */
function holdsText(holds: Cost, amount: number): string {
  const entries = costEntries(holds);
  const [only] = entries;
  if (entries.length === 1 && only)
    return t('action.holds', {
      n: only[1],
      resource: resourceName(only[0], only[1]).toLowerCase(),
    });
  return t('action.holds-blocks', { n: amount });
}

export function optionLook(option: TileOption, facts: TileFacts): ActionLook {
  const here = facts.kind;
  const enabled = option.blocked === null;
  const waiting = option.blocked === 'waiting' ? t('action.waiting') : null;
  switch (option.kind) {
    case 'collect': {
      const name = machineName(option.machine).toLowerCase();
      const empty = option.blocked === 'empty';
      const full = option.blocked === 'full';
      const holds =
        option.fit < option.amount
          ? t('action.fits', { fit: option.fit, n: option.amount })
          : holdsText(option.holds, option.amount);
      // the badge shows what it holds most of
      const most = costEntries(option.holds).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
      return {
        label: t('action.collect'),
        sub: waiting ?? (empty ? t('action.empty') : full ? t('action.full') : holds),
        aria: empty
          ? t('action.collect-empty-aria', { name })
          : t('action.collect-aria', { n: option.amount, resource: holds, name }),
        art: {
          tile: here,
          machine: option.machine,
          collect: most,
          // ready to harvest: the tile itself takes the machine's colour
          tint: enabled ? tintOf(MACHINE_COLOR[option.machine]?.ui) : undefined,
        },
        enabled,
        color: MACHINE_COLOR[option.machine]?.ui,
      };
    }
    case 'gather': {
      const block = resourceName(option.block);
      return {
        label: t('action.gather'),
        sub: waiting ?? (option.blocked === 'full' ? t('action.full') : block),
        aria: t('action.gather-aria', { block: block.toLowerCase() }),
        // the loose ground itself, its block in the badge; ready, it takes the block's colour
        art: {
          tile: here,
          collect: option.block,
          tint: enabled ? tintOf(RESOURCE_COLOR[option.block]) : undefined,
        },
        enabled,
        color: RESOURCE_COLOR[option.block],
      };
    }
    case 'load': {
      const type = facts.machine?.type ?? 'infuser';
      const [only] = option.blocks;
      const sub =
        waiting ??
        (option.blocked === 'none'
          ? t('action.load-none')
          : option.blocked === 'full'
            ? t('action.hopper-full')
            : option.blocks.length === 1 && only
              ? resourceName(only, 2)
              : t('action.load-kinds', { n: option.blocks.length }));
      return {
        label: t('action.load'),
        sub,
        aria: t('action.load-aria', { name: machineName(type).toLowerCase() }),
        // the infuser on its ground; ready, the tile takes the infuser's colour
        art: {
          tile: here,
          machine: type,
          tint: enabled ? tintOf(MACHINE_COLOR[type]?.ui) : undefined,
        },
        enabled,
        color: MACHINE_COLOR[type]?.ui,
      };
    }
    case 'info':
      return {
        label: t('action.info'),
        sub: null,
        aria: t('action.info-aria', { name: blueprintName(option.about).toLowerCase() }),
        art: { icon: 'bars' },
        enabled,
        color: MACHINE_COLOR[option.about.type]?.ui,
        ...(facts.machine && { gauge: gaugeOf(facts.machine) }),
      };
    case 'hub':
      return {
        label: t('action.hub'),
        sub: t('action.hub-sub'),
        aria: t('action.hub-aria'),
        art: { tile: KIND.HUB },
        enabled,
      };
  }
}
